import {
  Spinner,
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  useToastController,
  type ToastIntent,
  type useId,
} from "@fluentui/react-components";
import React from "react";

/**
 * Generic Fluent UI Toast hook
 */
export const useToast = (
  toasterId: ReturnType<typeof useId>,
  timeout = 5000,
  screenReaderLabel?: string
) => {
  const { dispatchToast, dismissAllToasts } = useToastController(toasterId);

  const notify = React.useCallback(
    (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) =>
      dispatchToast(
        <Toast>
          <ToastTitle data-testid="toast-title" action={headerButton}>
            {title}
          </ToastTitle>
          {/* This div is used to make the toast title accessible to screen readers. */}
          <div hidden>{screenReaderLabel}</div>
          {body && <ToastBody>{body}</ToastBody>}
          {footer && <ToastFooter>{footer}</ToastFooter>}
        </Toast>,
        { intent, position: "top", timeout }
      ),
    [dispatchToast, screenReaderLabel, timeout]
  );

  const info = React.useCallback(
    (
      title: string,
      body?: string,
      footer?: JSX.Element,
      pauseOnWindowBlur?: boolean
    ) =>
      dispatchToast(
        <Toast>
          <ToastTitle data-testid="toast-title" media={<Spinner size="tiny" />}>
            {title}
          </ToastTitle>
          {/* This div is used to make the toast title accessible to screen readers. */}
          <div hidden>{screenReaderLabel}</div>
          {body && <ToastBody>{body}</ToastBody>}
          {footer && <ToastFooter>{footer}</ToastFooter>}
        </Toast>,
        { position: "top", timeout, pauseOnWindowBlur }
      ),
    [dispatchToast, screenReaderLabel, timeout]
  );

  return { notify, info, dismissAllToasts };
};
